import React, { useEffect, useState } from "react";
import {
  Datagrid,
  Filter,
  FunctionField,
  Labeled,
  ResourceContextProvider,
  SearchInput,
  SelectInput,
  ShowProps,
  TextField,
  useDataProvider,
  useNotify,
} from "react-admin";
import {
  Grid,
  LinearProgress,
  makeStyles,
  Tab,
  Tabs,
  Typography,
} from "@material-ui/core";
import SanitizedGrid from "components/SanitizedGrid";
import { CompanyResources } from "utils/tools";
import CustomDateField from "components/CustomDateField";
import { paymentStatusFilterList } from "./conts";
import CustomPriceField from "components/CustomPriceField";
import PostedInvoicesTable from "pages/commercialDocuments/components/PostedInvoicesTable";
import FinancialInformationField from "./components/FinancialInformationField";
import StatusField from "components/StatusField";
import PaymentListPanel from "./components/PaymentListPanel";
import CreditNoteTable from "pages/commercialDocuments/components/CreditNoteTable";
import TabPanel from "pages/routeplans/components/TabPanel";
import PostedUntaggedCreditNotesTable from "pages/commercialDocuments/components/PostedUntaggedCreditNotesTable";
import CancelPaymentButton from "./components/CancelPaymentButton";
import UnapprovedInvoiceTable from "../unapprovedInvoices/components/UnapprovedInvoiceTable";
import UnapprovedCreditNoteTable from "../unapprovedCreditNotes/components/UnapprovedCreditNoteTable";
import { PaddingDenseStyled2List } from "../../components/ListWrapper";
import CustomPagination from "../../components/CustomPagination";

const PaymentHistoryFilter = (props: any) => (
  <Filter {...props}>
    <SearchInput source="search" alwaysOn />
    <SelectInput source="Status" choices={paymentStatusFilterList} alwaysOn />
  </Filter>
);

const useStyles = makeStyles({
  sidePanelTabs: {
    borderBottom: "1px solid #C4C4C4",
    "& .MuiTabs-fixed > .MuiTabs-indicator": {
      backgroundColor: "#989898 !important",
      height: "3px !important",
    },
  },
});

const Financial = (props: ShowProps) => {
  const [financialInformation, setFinancialInformation] =
    useState<IFinancialInformation>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [valueUnpostedTab, setValueUnpostedTab] = React.useState(0);
  const [valuePostedTab, setValuePostedTab] = React.useState(0);

  const classes = useStyles();

  const handleChangeUnpostedTab = (
    event: React.ChangeEvent<{}>,
    newValue: number,
  ) => {
    setValueUnpostedTab(newValue);
  };

  const handleChangePostedTab = (
    event: React.ChangeEvent<{}>,
    newValue: number,
  ) => {
    setValuePostedTab(newValue);
  };

  const unpostedProps = (index: number) => {
    return {
      id: `unposted-tab-${index}`,
      "aria-controls": `unposted-tabpanel-${index}`,
    };
  };

  const postedProps = (index: number) => {
    return {
      id: `posted-tab-${index}`,
      "aria-controls": `posted-tabpanel-${index}`,
    };
  };

  const { id } = props;
  const companyResources = CompanyResources(Number(id));
  const provider = useDataProvider();
  const notify = useNotify();

  const getFinancialInformation = () => {
    provider
      .customGetOne(`companies/${id}/financialInformation`)
      .then(({ data }: any) => {
        setFinancialInformation(data);
        setIsLoading(false);
      })
      .catch((error: any) => {
        notify(`Error from MBC : ${error.message}`, "error");
        setIsLoading(false);
      });
  };

  useEffect(() => {
    setIsLoading(true);
    getFinancialInformation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <SanitizedGrid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="h6">Financial Information</Typography>
        </Grid>

        {!isLoading ? (
          <Grid item xs={4}>
            <Labeled source="Total Outstanding">
              <FinancialInformationField
                source="balanceLcy"
                financialData={financialInformation}
              />
            </Labeled>
          </Grid>
        ) : (
          <Grid item xs={12}>
            <LinearProgress />
          </Grid>
        )}

        <Grid item xs={12}>
          <Tabs
            value={valueUnpostedTab}
            onChange={handleChangeUnpostedTab}
            aria-label="Unposted Commercial Document"
            className={classes.sidePanelTabs}
          >
            <Tab label="Unapproved Invoices" {...unpostedProps(0)} />
            <Tab label="Unapproved Credit Notes" {...unpostedProps(1)} />
          </Tabs>

          <TabPanel value={valueUnpostedTab} index={0}>
            <Grid item xs={12}>
              <UnapprovedInvoiceTable
                userId={companyResources.user.id}
                isFixedHeight
              />
            </Grid>
          </TabPanel>

          <TabPanel value={valueUnpostedTab} index={1}>
            <Grid item xs={12}>
              <UnapprovedCreditNoteTable
                userIds={companyResources.personInCharges.map(
                  (pic: IParent) => pic.user.id,
                )}
                isFixedHeight
              />
            </Grid>
          </TabPanel>
        </Grid>

        <Grid item xs={12}>
          <Tabs
            value={valuePostedTab}
            onChange={handleChangePostedTab}
            aria-label="Posted Commercial Document"
            className={classes.sidePanelTabs}
          >
            <Tab label="Posted Invoices" {...postedProps(0)} />
            <Tab label="Untagged Credit Notes" {...postedProps(1)} />
            <Tab
              label="Posted Untagged Credit Notes (need finance)"
              {...postedProps(2)}
            />
          </Tabs>

          <TabPanel value={valuePostedTab} index={0}>
            <Grid item xs={12}>
              <PostedInvoicesTable
                mbcCustomerId={companyResources.user.mbcCustomerId}
              />
            </Grid>
          </TabPanel>

          <TabPanel value={valuePostedTab} index={1}>
            <Grid item xs={12}>
              <CreditNoteTable
                isUntagged
                mbcCustomerId={companyResources.user.mbcCustomerId}
              />
            </Grid>
          </TabPanel>

          <TabPanel value={valuePostedTab} index={2}>
            <Grid item xs={12}>
              <PostedUntaggedCreditNotesTable
                mbcCustomerId={companyResources.user.mbcCustomerId}
              />
            </Grid>
          </TabPanel>
        </Grid>

        <Grid item xs={12}>
          <Typography variant="h6">Payment History</Typography>
        </Grid>

        <Grid item xs={12}>
          <ResourceContextProvider value="payments">
            <PaddingDenseStyled2List
              {...props}
              exporter={false}
              hasCreate={false}
              actions={null}
              filters={<PaymentHistoryFilter />}
              filterDefaultValues={{
                UserIds: companyResources.personInCharges.map(
                  (pic: IParent) => pic.user.id,
                ),
              }}
              sort={{ field: "createdAt", order: "DESC" }}
              bulkActionButtons={false}
              title=" "
              pagination={<CustomPagination />}
            >
              <Datagrid expand={<PaymentListPanel />}>
                <TextField source="id" />
                <TextField source="paymentPlatform" emptyText="-" />
                <TextField source="description" />
                <CustomPriceField source="amount" />
                <CustomDateField source="createdAt" />
                <StatusField source="status" />
                <FunctionField
                  label="Action"
                  render={(record: any) => {
                    return (
                      <>
                        {/* <RecreatePaymentLinkButton /> */}
                        {/* <RetryPaymentButton id={record.id}/> */}

                        {record.status === "new" && (
                          <CancelPaymentButton id={record.id} />
                        )}

                        {/* {record.status !== "paid" &&
                          record.status !== "posted" && (
                            <ConfirmPaymentButton id={record.id} />
                          )} */}
                      </>
                    );
                  }}
                />
              </Datagrid>
            </PaddingDenseStyled2List>
          </ResourceContextProvider>
        </Grid>
      </SanitizedGrid>
    </>
  );
};

export default Financial;
